import { AppCircularProgress } from '../../../components/common/loaders/AppCircularProgress';
import {
  GoogleMap,
  Libraries,
  useLoadScript,
} from '@react-google-maps/api';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  containerStyle,
  libraries,
  polygonType,
  DEFAULT_MAP_OPTIONS,
  TMapProps,
} from '../../../types/mvpMap';
import { latLngConversion } from '../../../utils/functions/maps';
import { useTiffHelper } from './tiff';
import { Box } from '@mui/material';
import PolygonUnit from './polygonUnit';

const GoogleMapViewComparative = ({
  tiffFileUrl,
  geometryHex,
  options,
  indexName,
  selectedDate,
  setCenterValue,
}: TMapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(
    null
  );
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env
      .REACT_APP_GOOGLE_MAPS_API_KEY as string, // Use your Google Maps API key
    libraries: libraries as Libraries,
  });

  const calculatePolygonCenter = (
    polygon: polygonType[]
  ) => {
    let latSum = 0;
    let lngSum = 0;
    const numPoints = polygon?.length;
    polygon.forEach((point) => {
      latSum += point.lat;
      lngSum += point.lng;
    });
    const centerLat = latSum / numPoints;
    const centerLng = lngSum / numPoints;
    return { lat: centerLat, lng: centerLng };
  };

  const polygon = useMemo(
    () =>
      latLngConversion(geometryHex) as {
        type: string | undefined;
        geometry: polygonType[];
      },
    [geometryHex]
  );

  const polygonGeometry = polygon.geometry;
  const center = calculatePolygonCenter(polygonGeometry);

  useEffect(() => {
    if (polygonGeometry.length > 0) {
      const newCenter =
        calculatePolygonCenter(polygonGeometry);
      if (setCenterValue) {
        setCenterValue((prev) => {
          if (
            !prev ||
            prev.lat !== newCenter.lat ||
            prev.lng !== newCenter.lng
          ) {
            return newCenter;
          }
          return prev; // Prevent unnecessary re-renders
        });
      }
    }
  }, [polygonGeometry, setCenterValue]); // Add dependencies

  const { onMapLoad } = useTiffHelper();

  useEffect(() => {
    if (map) {
      onMapLoad(
        map,
        tiffFileUrl,
        polygon.geometry,
        indexName
      );
    }
  }, [tiffFileUrl, polygon, indexName]);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  return (
    <>
      {!isLoaded ? (
        <AppCircularProgress />
      ) : (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          options={options || DEFAULT_MAP_OPTIONS}
          onLoad={(map) => {
            setMap(map);
            onMapLoad(
              map,
              tiffFileUrl,
              polygonGeometry,
              indexName
            );
          }}
          onUnmount={onUnmount}>
          {selectedDate !== undefined && (
            <Box //k
              sx={{
                position: 'absolute',
                margin: '8px 12px',
                background: '#fff',
                padding: '4px 8px',
                zIndex: '1',
                borderRadius: '4px',
              }}>
              {selectedDate.format('DD/MM/YYYY')}
            </Box>
          )}
          {/* Add the polygon on the map */}
          <PolygonUnit polygonCoords={polygonGeometry} />
        </GoogleMap>
      )}
    </>
  );
};

export default React.memo(GoogleMapViewComparative);
